import {
  FC,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { ExpandedState, Row } from '@tanstack/react-table';
import { Location, LocationFilled } from '@carbon/react/icons';
import { Button, InlineNotification, Link } from '@carbon/react';

import {
  DataTable,
  type DataTableColumnDef,
  type DataTableSelectionState,
} from 'components/data-table';
import type {
  DataTableConfig,
  ExportCallback,
  TableState,
} from 'components/data-table/types';
import { FormattedCount } from 'components/formatted-count';
import {
  subtractCoverageLabel,
  subtractCoverageSuffix,
} from 'utils/subtract-coverage';

import type { AvailableAssetSubtract, DraftAssetRow } from '../types';
import {
  draftAssetRowId,
  heldInPartRowIds,
  PREVIEW_TABLE_CONFIG,
} from '../utils';
import styles from './assets-table-view.module.scss';

/**
 * "(2 of 3 Subtracts)" for a Tract the draft holds in part; null for any other
 * row, a Subtract row among them (it has no Subtracts of its own). Counted as
 * the server counts it, as Step 2 does, so it shows even where the index lags
 * Salesforce; from the row's own Subtract rows only for a Tract the server
 * holds whole and the user has just ticked in part. Ticks that make the Tract
 * whole, or take all of it out, hide it.
 *
 * @param {DraftAssetRow} row - An asset or Subtract row
 * @return {string | null} The label
 */
const heldInPartLabel = (row: DraftAssetRow): string | null => {
  if (!Array.isArray(row.subtracts)) return null;
  const held =
    row.includedSubtractCount ??
    row.subtracts.filter((subtract) => subtract.selected).length;
  const total = row.subtractCount ?? row.subtracts.length;
  return held > 0 && held < total ? subtractCoverageLabel(held, total) : null;
};

/**
 * Inline column-filter rules:
 *   - Six BE-supported text permits get a `text` filter UI: `name` / `entity`
 *     / `surveyArea` / `quarterCall` / `instrumentType` / `netAcreageUnits`.
 *   - Decimal / date columns are gated with `enableColumnFilter: false`
 *     because the inline range UIs don't match the BE shapes today.
 */
// Sortable iff the BE's `Query::Crm::TractsAndSubtracts::Elastic`
// SORT_FIELD_MAP exposes the column. Out-of-allowlist values raise
// ArgumentError on the BE; mark those columns `enableSorting: false`
// so DataTable doesn't render a sort indicator the user can click.
const STATIC_COLUMNS: DataTableColumnDef<DraftAssetRow>[] = [
  {
    id: 'name',
    accessorKey: 'name',
    header: 'Name',
    meta: { type: 'text' as const },
    // Both tract rows and subtract sub-rows carry `salesforceId`; route to
    // the full-page CRM Tract Wizard edit route in a new tab. Wellbore /
    // LTD rows (which have no salesforceId) render as plain text. A Tract
    // held in part says how much of it the draft holds.
    cell: ({ row, getValue }) => {
      const display = getValue<string | null>();
      if (!display) return '—';
      const sfId = row.original.salesforceId;
      if (!sfId) return display;
      const label = heldInPartLabel(row.original);
      return (
        <>
          <Link
            href={`/crm/tracts_and_subtracts/${encodeURIComponent(sfId)}/edit`}
            target="_blank"
            rel="noopener noreferrer"
          >
            {display}
          </Link>
          {subtractCoverageSuffix(label)}
        </>
      );
    },
  },
  {
    id: 'assetType',
    accessorKey: 'assetType',
    header: 'Asset Type',
    enableColumnFilter: false,
  },
  {
    id: 'entity',
    header: 'Entity',
    cell: ({ row }) => row.original.entities[0]?.name ?? '—',
    meta: { type: 'text' as const },
    // BE sorts entity_name as a nested field — expose later if needed.
    enableSorting: false,
  },
  {
    id: 'instrumentType',
    accessorKey: 'instrumentType',
    header: 'Instrument Type',
    meta: { type: 'text' as const },
  },
  {
    id: 'surveyArea',
    accessorKey: 'surveyArea',
    header: 'Location',
    meta: { type: 'text' as const },
    cell: ({ row }) => {
      const value = row.original.surveyArea;
      if (!value) return '—';
      const uwi = row.original.uwi;
      if (row.original.assetType === 'Wellbore' && uwi) {
        return (
          <Link
            href={`/wells/${uwi}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            {value}
          </Link>
        );
      }
      return value;
    },
  },
  {
    id: 'quarterCall',
    accessorKey: 'quarterCall',
    header: 'Quarter Call',
    meta: { type: 'text' as const },
  },
  {
    id: 'netAcres',
    accessorKey: 'netAcres',
    header: 'Net Acres',
    meta: { type: 'decimal' as const },
    enableColumnFilter: false,
  },
  {
    id: 'netAcreageUnits',
    accessorKey: 'netAcreageUnits',
    header: 'Net Acreage Units',
    meta: { type: 'text' as const },
  },
  {
    id: 'grossAcres',
    accessorKey: 'grossAcres',
    header: 'Gross Acres',
    meta: { type: 'decimal' as const },
    enableColumnFilter: false,
  },
  // NRI / WI columns: the BE jbuilder emits nil placeholders today — the
  // cells render '—' via the DataTable's null formatter.
  {
    id: 'nri',
    accessorKey: 'nri',
    header: 'NRI',
    meta: {
      type: 'decimal' as const,
      format: { minDecimalPlaces: 8, maxDecimalPlaces: 8 },
    },
    enableColumnFilter: false,
    enableSorting: false,
  },
  {
    id: 'workingInterest',
    accessorKey: 'workingInterest',
    header: 'WI',
    meta: {
      type: 'decimal' as const,
      format: { minDecimalPlaces: 8, maxDecimalPlaces: 8 },
    },
    enableColumnFilter: false,
    enableSorting: false,
  },
  {
    id: 'createdAt',
    accessorKey: 'createdAt',
    header: 'Created Date',
    meta: { type: 'date' as const },
    enableColumnFilter: false,
  },
  {
    id: 'updatedAt',
    accessorKey: 'updatedAt',
    header: 'Updated Date',
    meta: { type: 'date' as const },
    enableColumnFilter: false,
  },
];

/**
 * The same columns with every owning entity in the Entity cell, for a draft
 * seeded from an asset list: its rows carry every owner of a Tract, where the
 * other sources keep the first. Joined with "; " as the CSV export joins them,
 * since an owner's name can hold a comma.
 */
const ALL_ENTITY_COLUMNS: DataTableColumnDef<DraftAssetRow>[] =
  STATIC_COLUMNS.map((column) =>
    column.id === 'entity'
      ? {
          ...column,
          cell: ({ row }) =>
            row.original.entities
              .map((entity) => entity.name)
              .filter(Boolean)
              .join('; ') || '—',
        }
      : column
  );

const EMPTY_SET: Set<string> = new Set();
// Stable empty selection so a selectable table stays CONTROLLED even before `selectionState`
// arrives — keeps the control decision keyed on `hasSelection` alone (no controlled↔
// uncontrolled flip while a server-driven `selectionState` resolves late).
const EMPTY_SELECTION: DataTableSelectionState = {
  selectAll: false,
  toggledNodes: [],
};

// Every customization feature off — the toolbar renders if ANY of
// search/filters/export/sorting/resizing/reordering/pinning/visibility is
// enabled. We want an inline row list, not a full table, under its parent's
// header.
const SUB_TABLE_CONFIG: DataTableConfig = {
  ...PREVIEW_TABLE_CONFIG,
  hideHeader: true,
};

/**
 * Expanded sub-row body. Renders a parent tract's `subtracts[]` as rows
 * using the exact same column set as the parent table — matching the v5
 * mock's column-aligned child layout. Sub-tracts carry the parent's
 * `entities` and `instrumentType` (BE jbuilder inherits them), and their
 * `assetType === 'Sub-Tract'` feeds the "Asset Type" column directly.
 *
 * With `onSelect` (Step 1) it lists every Subtract, so one left out can be
 * ticked back, each with a checkbox when the Tract has more than one (a lone
 * Subtract is the Tract, ticked on its row). Without it (Step 2), only the
 * Subtracts the draft holds.
 *
 * The cast to `DraftAssetRow` is safe because the only field not on
 * `AvailableAssetSubtract` is `subtracts[]`, which no column accessor
 * references.
 *
 * @param {AvailableAssetSubtract[]} props.subtracts - Child rows from the parent row
 * @param {DataTableColumnDef[]} props.columns - The parent table's value columns
 * @param {Function} [props.onSelect] - Ticks or unticks a Subtract
 * @param {boolean} [props.selectionDisabled] - Holds the checkboxes
 * @return {JSX.Element} The expanded sub-row content
 */
const AssetSubtractsSubTable: FC<{
  subtracts: AvailableAssetSubtract[];
  columns: DataTableColumnDef<DraftAssetRow>[];
  onSelect?: (subtractId: string, selected: boolean) => void;
  selectionDisabled?: boolean;
}> = ({ subtracts, columns, onSelect, selectionDisabled = false }) => {
  const shown = useMemo(
    () => (onSelect ? subtracts : subtracts.filter((s) => s.selected)),
    [onSelect, subtracts]
  );
  const selectable = onSelect !== undefined && subtracts.length > 1;
  const selection = useMemo<DataTableSelectionState>(
    () => ({
      selectAll: false,
      toggledNodes: shown.filter((s) => s.selected).map((s) => s.salesforceId),
    }),
    [shown]
  );
  const handleSelectionChange = useCallback(
    (state: TableState) => {
      const ticked = new Set(state.selection.toggledNodes);
      for (const subtract of shown) {
        const selected = ticked.has(subtract.salesforceId);
        if (selected !== subtract.selected) {
          onSelect?.(subtract.salesforceId, selected);
        }
      }
    },
    [shown, onSelect]
  );

  if (shown.length === 0) {
    return <div className={styles.subEmpty}>No sub-tracts</div>;
  }

  const rows = shown as unknown as DraftAssetRow[];

  return (
    <div className={styles.subTableWrap}>
      <DataTable<DraftAssetRow>
        data={rows}
        columns={columns}
        getRowId={draftAssetRowId}
        config={
          selectable
            ? {
                ...SUB_TABLE_CONFIG,
                selectionType: 'multi' as const,
                isBulkSelectEnabled: false,
              }
            : SUB_TABLE_CONFIG
        }
        {...(selectable && {
          controlledState: { selection },
          onSelectionChange: handleSelectionChange,
          disableSelection: selectionDisabled,
        })}
      />
    </div>
  );
};

/** Props for {@link AssetsTableView}. */
export interface AssetsTableViewProps {
  /** Asset rows from the current fetch result. */
  data: DraftAssetRow[];
  /** Server-paged total used by `DataTable`'s pagination footer. */
  totalCount: number;
  /** True while a fetch is in flight. */
  isLoading: boolean;
  /** Banner copy. Null hides the notification. */
  errorMessage: string | null;
  /**
   * Optional info-level banner copy shown in place of the error
   * notification while a longer-running operation is in flight (e.g.
   * a bulk select-all that triggered the materializer). Null hides
   * the notification.
   */
  infoMessage?: string | null;
  /** Number rendered in the table header. Omit to hide the header entirely. */
  headerCount?: number;
  /** When true, header reads "Assets (N selected)" instead of "Assets (N)". */
  showSelectedSuffix?: boolean;
  /** DataTable's paginated load callback. */
  loadData: (params: TableState) => void;
  /** Search-input placeholder. */
  searchPlaceholder?: string;
  /** Currently-focused asset id; styled as the pinned row when `onPinToggle` is set. */
  focusedAssetId?: string | null;
  /** Pin-toggle handler. When omitted, the pin column is not rendered. */
  onPinToggle?: (id: string) => void;
  /** Cross-page selection state. When omitted, the selection column is not rendered. */
  selectionState?: DataTableSelectionState;
  /** Row-toggle handler from DataTable. When omitted, the selection column is not rendered. */
  onRowSelect?: (next: DataTableSelectionState) => void;
  /** Disable selection inputs while a mutation is in flight. */
  selectionDisabled?: boolean;
  /** Row ids to render with the highlight class. */
  highlightedRowIds?: Set<string>;
  /**
   * Server-side export callback. When set, the export button POSTs the
   * filter/sort scope to the BE and downloads the CSV stream rather
   * than building one client-side from the in-view rows.
   */
  onExport?: ExportCallback;
  /** Disables the export button and is shown as its tooltip. */
  exportDisabledReason?: string;
  /**
   * Show every owning entity in the Entity cell, not the first: the draft was
   * seeded from an asset list, whose rows carry every owner of a Tract.
   */
  allEntities?: boolean;
  /**
   * Right-aligned actions rendered next to the "Assets (N selected)"
   * heading. Per the v5 mock, the Add Wellbore / Add LTD / Add CRM Tract
   * buttons live on the assets table header — not inline with the
   * search bar, and not on the slots table.
   */
  headerActions?: ReactNode;
  /** Row ids whose checkbox shows a Tract held in part (selectable table only). */
  indeterminateRowIds?: Set<string>;
  /**
   * Step 1: ticks or unticks a Subtract. Set, a Tract's Subtract rows list
   * every Subtract, with checkboxes when it has more than one; omitted, only
   * the Subtracts the draft holds.
   */
  onSubtractSelect?: (subtractId: string, selected: boolean) => void;
  /** Holds the Subtract checkboxes, as `selectionDisabled` holds the rows'. */
  subtractSelectionDisabled?: boolean;
}

/**
 * Pure view layer for the assets table. Owns nothing about data fetching,
 * mutations, or step-specific behavior — every piece comes in via props so
 * one component can back both the Step 1 selectable table and the Step 2
 * read-only review table.
 *
 * Conditional columns:
 *   - `onPinToggle` set → prepend a pin-action column. Omitted → no pin column.
 *   - `onRowSelect` set → enable `selectionType: 'multi'` and plumb
 *     selection state. Omitted → no selection column at all
 *     (`DataTable` skips it when `selectionType` is omitted).
 *
 * @param {AssetsTableViewProps} props - Data + chrome + optional wiring
 * @return {JSX.Element} The assets table view
 */
export const AssetsTableView: FC<AssetsTableViewProps> = ({
  data,
  totalCount,
  isLoading,
  errorMessage,
  infoMessage,
  headerCount,
  showSelectedSuffix,
  loadData,
  searchPlaceholder,
  focusedAssetId,
  onPinToggle,
  selectionState,
  onRowSelect,
  selectionDisabled,
  highlightedRowIds,
  onExport,
  exportDisabledReason,
  allEntities = false,
  headerActions,
  indeterminateRowIds,
  onSubtractSelect,
  subtractSelectionDisabled,
}) => {
  const valueColumns = allEntities ? ALL_ENTITY_COLUMNS : STATIC_COLUMNS;
  const columns = useMemo<DataTableColumnDef<DraftAssetRow>[]>(() => {
    if (!onPinToggle) return valueColumns;
    return [
      {
        id: 'pin-action',
        header: '',
        cell: ({ row }) => {
          // Pin maps to a tract polygon; non-tract rows have no geometry, so
          // hide the button rather than render a no-op for those rows.
          if (
            row.original.assetType !== 'Tract' ||
            !row.original.salesforceId
          ) {
            return null;
          }
          const sfId = row.original.salesforceId;
          const isPinned = sfId === focusedAssetId;
          return (
            <Button
              kind="ghost"
              size="sm"
              hasIconOnly
              renderIcon={isPinned ? LocationFilled : Location}
              iconDescription={isPinned ? 'Unfocus on map' : 'Focus on map'}
              tooltipPosition="right"
              className={styles.pinButton}
              onClick={() => onPinToggle(sfId)}
            />
          );
        },
      },
      ...valueColumns,
    ];
  }, [focusedAssetId, onPinToggle, valueColumns]);

  const renderRowSubComponent = useCallback(
    (row: Row<DraftAssetRow>) => (
      <AssetSubtractsSubTable
        subtracts={row.original.subtracts}
        columns={valueColumns}
        onSelect={onSubtractSelect}
        selectionDisabled={subtractSelectionDisabled}
      />
    ),
    [valueColumns, onSubtractSelect, subtractSelectionDisabled]
  );

  // A Tract held in part opens expanded the first time it shows held in part,
  // so its Subtracts are in view; every other row starts closed, and any row
  // opens and closes as usual.
  const [expanded, setExpanded] = useState<ExpandedState>({});
  const openedRef = useRef(new Set<string>());
  useEffect(() => {
    const fresh = heldInPartRowIds(data).filter(
      (id) => !openedRef.current.has(id)
    );
    if (fresh.length === 0) return;
    fresh.forEach((id) => openedRef.current.add(id));
    setExpanded((prev) =>
      prev === true
        ? prev
        : { ...prev, ...Object.fromEntries(fresh.map((id) => [id, true])) }
    );
  }, [data]);
  const handleExpansionChange = useCallback(
    (state: TableState) => setExpanded(state.expanded),
    []
  );

  const getRowCanExpand = useCallback(
    (row: Row<DraftAssetRow>) => row.original.subtracts.length > 0,
    []
  );

  const hasSelection = onRowSelect !== undefined;

  return (
    <div className={styles.container}>
      {headerCount !== undefined && (
        <header className={styles.tableHeader}>
          <span className={styles.tableHeaderTitle}>
            Assets (<FormattedCount value={headerCount} />
            {showSelectedSuffix ? ' selected' : ''})
          </span>
          {headerActions !== undefined && (
            <div className={styles.headerActions}>{headerActions}</div>
          )}
        </header>
      )}
      {errorMessage !== null && (
        <InlineNotification
          kind="error"
          title="Assets table error"
          subtitle={errorMessage}
          hideCloseButton
          lowContrast
          className={styles.errorNotification}
        />
      )}
      {errorMessage === null &&
        infoMessage !== null &&
        infoMessage !== undefined && (
          <InlineNotification
            kind="info"
            title="Working…"
            subtitle={infoMessage}
            hideCloseButton
            lowContrast
            className={styles.errorNotification}
          />
        )}
      <div className={styles.tableWrap}>
        <DataTable<DraftAssetRow>
          data={data}
          columns={columns}
          getRowId={draftAssetRowId}
          isLoading={isLoading}
          scroll={{ mode: 'paginated', serverDriven: true, totalCount }}
          onPaginationChange={loadData}
          onSortChange={loadData}
          onFilterChange={loadData}
          onGlobalSearchChange={loadData}
          config={{
            globalSearch: true,
            columnVisibility: true,
            columnFilters: true,
            export: onExport
              ? {
                  exportPresets: [
                    {
                      name: 'Export',
                      format: 'csv',
                      onExport,
                      disabledReason: exportDisabledReason,
                    },
                  ],
                }
              : true,
            sorting: true,
            ...(hasSelection && { selectionType: 'multi' as const }),
          }}
          searchPlaceholder={searchPlaceholder}
          renderRowSubComponent={renderRowSubComponent}
          getRowCanExpand={getRowCanExpand}
          initialTableState={{
            sortColumn: 'name',
            sortDirection: 'asc',
          }}
          // Expansion is always controlled, so a Tract held in part can open
          // itself. Selection is controlled when the table is selectable — the
          // parent owns `selectionState`; the table reflects it and reports clicks
          // via `onRowSelect`. The control decision is stable per mount
          // (`hasSelection` = `onRowSelect` set).
          controlledState={{
            expanded,
            ...(hasSelection && {
              selection: selectionState ?? EMPTY_SELECTION,
            }),
          }}
          onExpansionChange={handleExpansionChange}
          {...(hasSelection && {
            disableSelection: selectionDisabled,
            onSelectionChange: onRowSelect
              ? (s: TableState) => onRowSelect(s.selection)
              : undefined,
            highlightedRowIds: highlightedRowIds ?? EMPTY_SET,
            indeterminateRowIds,
          })}
        />
      </div>
    </div>
  );
};
